import * as yup from 'yup';

export default (
  isOnDemandPayEnabled: boolean,
  isReserveEnabled: boolean,
  individualGlobalAllowance?: number,
  payCalendarGlobalAllowance?: number
) =>
  yup
    .object()
    .shape({
      odpAllowancePercentage: yup.number().when([], {
        is: () => isOnDemandPayEnabled,
        then: (schema) =>
          schema
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .required('On-demand pay allowance is required when ODP module is enabled')
            .test(
              'valid-odp-allowance-range',
              'Please use a value from 0 to 100',
              function validateOdpAllowanceRange(value) {
                // Allow 200 as it represents "Custom %" option
                if (value === 200) {
                  return true;
                }
                // For other values, check if they're between 0-100
                return value >= 0 && value <= 100;
              }
            )
            .integer('Please provide a whole number value from 0 to 100'),
        otherwise: (schema) => schema.notRequired(),
      }),
      customOdpAllowancePercentage: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('odpAllowancePercentage', {
          is: 200, // When odpAllowancePercentage is 200 (custom option)
          then: (schema) =>
            schema
              .required('Value is required when choosing a Custom %')
              .min(0, 'Please use a value from 0 to 100')
              .max(100, 'Please use a value from 0 to 100')
              .integer('Please provide a whole number value from 0 to 100'),
          otherwise: (schema) => schema.notRequired(),
        }),
      globalAllowancePercentage: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .required('Global allowance is always required')
        .test(
          'valid-global-allowance-range',
          'Please use a value from 0 to 100',
          function validateGlobalAllowanceRange(value) {
            // Allow 200 as it represents "Custom %" option
            if (value === 200) {
              return true;
            }
            // For other values, check if they're between 0-100
            return value >= 0 && value <= 100;
          }
        )
        .integer('Please provide a whole number value'),
      customGlobalAllowancePercentage: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('globalAllowancePercentage', {
          is: 200, // When globalAllowancePercentage is 200 (custom option)
          then: (schema) =>
            schema
              .required('Value is required when choosing a Custom %')
              .min(0, 'Please use a value from 0 to 100')
              .max(100, 'Please use a value from 0 to 100')
              .integer('Please provide a whole number value'),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .test(
      'sub-limit-not-higher-than-global',
      'On-demand pay sub-limit cannot be higher than Global limit',
      function validateSubLimitNotHigherThanGlobal(values) {
        const {
          odpAllowancePercentage = 0,
          customOdpAllowancePercentage = 0,
          globalAllowancePercentage = 0,
          customGlobalAllowancePercentage = 0,
        } = values;

        // Calculate the actual values
        const actualOdpValue =
          odpAllowancePercentage === 200
            ? customOdpAllowancePercentage ?? 0
            : odpAllowancePercentage ?? 0;

        const actualGlobalValue =
          globalAllowancePercentage === 200
            ? customGlobalAllowancePercentage ?? 0
            : globalAllowancePercentage ?? 0;

        // Determine the effective global limit (individual first, then pay calendar)
        const effectiveGlobalLimit = individualGlobalAllowance ?? payCalendarGlobalAllowance ?? 0;

        // Use the form's global value if it's being set, otherwise use the effective limit
        const currentGlobalLimit = actualGlobalValue > 0 ? actualGlobalValue : effectiveGlobalLimit;

        // Check if ODP allowance is higher than the effective global allowance
        if (actualOdpValue > currentGlobalLimit && currentGlobalLimit > 0) {
          // If it's a custom value, attach error to customOdpAllowancePercentage field
          // Otherwise, attach to odpAllowancePercentage dropdown
          const errorPath =
            odpAllowancePercentage === 200
              ? 'customOdpAllowancePercentage'
              : 'odpAllowancePercentage';
          return this.createError({
            path: errorPath,
            message: 'On-demand pay sub-limit cannot be higher than Global limit',
          });
        }
        return true;
      }
    );
